import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { usePagination } from "@/lib/hooks/use-pagination";
import { useProductAllocationsOverview } from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { TableCardSkeleton } from "../../skeleton-loaders";
import { useProductsTabContext } from "../products-tab-context";
import { T } from "./page.i18n";

const formatWeight = (weight: number) => {
  const rounded = Math.round(weight * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
};

export default function ProductsAssetClassesPage() {
  const {
    collectionId,
    debouncedSearch,
    openEdit,
    openDelete,
    loadingProductId,
    checkingDeleteId,
  } = useProductsTabContext();
  const { allocations, isLoading } = useProductAllocationsOverview(collectionId, debouncedSearch);
  const t = useT(T);
  const localize = useLocalize();
  const {
    page,
    setPage,
    pageSize,
    totalCount,
    totalPages,
    pageItems: paginatedAllocations,
  } = usePagination(allocations, 10);

  if (isLoading && allocations.length === 0 && !debouncedSearch) {
    return <TableCardSkeleton columns={3} />;
  }

  if (allocations.length === 0 && debouncedSearch.trim() !== "") {
    return <EmptyState>{t.noProductsMatch}</EmptyState>;
  }

  if (allocations.length === 0) {
    return <EmptyState>{t.noProducts}</EmptyState>;
  }

  return (
    <>
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.productName}</TableHead>
            <TableHead>{t.assetAllocation}</TableHead>
            <TableActionsHead>{t.actions}</TableActionsHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedAllocations.map((row) => (
            <TableRow key={row.productId}>
              <TableCell className="font-medium">{localize(row.nameI18n)}</TableCell>
              <TableCell>
                {row.allocations.length === 0 ? (
                  <span className="text-muted-foreground italic text-xs">{t.notSet}</span>
                ) : (
                  <ul className="space-y-0.5">
                    {row.allocations.map((a) => (
                      <li key={a.assetClassId} className="text-sm">
                        <span className="font-medium">{localize(a.assetClassNameI18n)}</span>{" "}
                        <span className="text-muted-foreground">{formatWeight(a.weight)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </TableCell>
              <TableActionsCell>
                <Button
                  variant="ghost"
                  size="icon"
                  tooltip={t.edit}
                  onClick={() => openEdit(row.productId)}
                  loading={loadingProductId === row.productId}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  tooltip={t.delete}
                  loading={checkingDeleteId === row.productId}
                  onClick={() => openDelete({ id: row.productId, nameI18n: row.nameI18n })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableActionsCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </>
  );
}
